import type { PalworldItem } from "@/interfaces/CardItem";
import type { SrcType } from "@/models/alt-arts";
import type { RawAaCard } from "@/lib/api/cards-db";
import type { PalworldAaData } from "@/lib/games/palworld/getCardData";
import { getPalworldCardImage } from "@/lib/games/palworld/getCardData";

/**
 * The synchronous card-data interface every game handler implements.
 *
 * Palworld differs from the other games in where its cards come from: they are
 * fetched from tcgfan-backend at request time rather than imported from a
 * bundled json file, so this module cannot hold them at import time the way
 * lib/games/naruto/getCardDataLocal.tsx does.
 *
 * Pages that render cards fetch both payloads server-side and hand them to
 * `setPalworldCards` before the first render, so every lookup below reads the
 * same data the API served -- there is no bundled copy to fall back to.
 */

let CARD_DATA: Record<string, PalworldItem> = {};
let AA_DATA: PalworldAaData = {};

/**
 * Check if a card is an alternate art (SR, SP, SSP suffix) based on its ID
 */
function isAlternateArt(cardId: string): boolean {
  return /(?:SR|SP|SSP)$/i.test(cardId);
}

export function setPalworldCards(
  cards: PalworldItem[],
  aaData: PalworldAaData = {},
): void {
  CARD_DATA = Object.fromEntries(cards.map((card) => [card.id, card]));
  AA_DATA = aaData;
}

/**
 * Only the full-size webp is stored; imgproxy derives every other size from it.
 * `src` is accepted for interface parity -- Palworld has one printing per card.
 */
export const getCardImage = (
  cardId: string,
  size?: string,
  _src: SrcType | "" = "",
): string => getPalworldCardImage(cardId, size);

export const getCardData = (cardId: string): PalworldItem | undefined =>
  CARD_DATA[cardId];

/**
 * The printings of a card.
 *
 * For Palworld, SR/SP/SSP cards are treated as alternate arts.
 * When showAAs is false, SR/SP/SSP cards return empty array (filtered out).
 * When showAAs is true, all cards including SR/SP/SSP variants are shown.
 *
 * `showAAs` is the user's "show alternate arts" toggle.
 */
export const getAltArts = (
  cardId: string,
  showAAs: boolean = true,
): RawAaCard[] => {
  // If showAAs is false and this is an AA card, don't show it
  if (!showAAs && isAlternateArt(cardId)) {
    return [];
  }

  const printings = AA_DATA[cardId];
  if (!printings?.length) return [];

  // TCGplayer rows describe a product, not a printing to draw, and `hide` marks
  // a printing the site does not offer.
  const visible = printings.filter(
    (printing) => printing.src !== "tcgplayer" && !printing.hide,
  );

  return visible.slice(0, 1);
};

export const getAACardId = (aaItem: any): string => aaItem?.id ?? "";

export const getAACardIdData = (aaCardId: string): PalworldItem | undefined =>
  CARD_DATA[aaCardId];

/**
 * The printing behind a card tile. Callers read `id`, `src` and `name` off it to
 * build the image url, so this returns the aa row rather than the card.
 */
export const getAAFromCardId = (aaCardId: string): RawAaCard | undefined =>
  getAltArts(aaCardId)[0];

export const cleanCardData = (): Record<string, PalworldItem> => CARD_DATA;
